import { Suspense, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  ActionConfirmationModal,
  Button,
  CreateCertificateModal,
  SectionLayout,
  Typography,
} from '@breezeai-frontend/cargo-ui';
import { useMutation, useQuery } from '@tanstack/react-query';
import invariant from 'tiny-invariant';

import { useFeatureToggle, useUser } from '../../../context/auth/auth-hooks';
import { usePlatform } from '../../../context/PlatformContext';
import { CertificateOnlyApi } from '../../../generated/api-client';
import { getBffApiConfiguration } from '../../../network/AuthRequests';
import { useTracking } from '../../../utils/snowplow/hooks';
import { ActiveOpenCoverTooltip } from '../../components/ActiveOpenCoverTooltip/ActiveOpenCoverTooltip';
import { CertificatePdfOverrideModal } from '../../components/CertificatePdfOverrideModal/CertificatePdfOverrideModal';
import { useCreateCertificatePdfOverride } from '../../hooks/useCerificatePdfOverride';
import { StatusBanner } from '../CertificatesPage/CertificatesTable/StatusBanner/StatusBanner';
import { certificateQueries } from '../network/queries';
import { type CertificatesRouteParams } from '../types';
import { CertificateActions } from './CertificateActions/CertificateActions';
import { CertificateAdditionalClauses } from './CertificateAdditionalClauses/CertificateAdditionalClauses';
import { CertificateClaims } from './CertificateClaims/CertificateClaims';
import styles from './CertificateDetailsPage.module.scss';
import { CertificateShipmentDetails } from './CertificateShipmentDetails/CertificateShipmentDetails';
import { NamedAssuredDetails } from './NamedAssuredDetails/NamedAssuredDetails';
import { PollingFileList } from './PollingFileList/PollingFileList';

const useCancelCertificate = () => {
  const navigate = useNavigate();

  const mutation = useMutation({
    mutationFn: async (data: {
      reason: string;
      policyId: number;
      userId: number;
    }) => {
      const apiConfig = await getBffApiConfiguration();
      return new CertificateOnlyApi(apiConfig).cancelCertificateOnly({
        certificateOnlyId: data.policyId,
        bffWtwFrontendCancelPolicyRequest: {
          cancel_description: data.reason,
          user_id: data.userId,
        },
      });
    },
    onSuccess: () => {
      navigate('/certificates?cancelled=true');
    },
  });
  return mutation;
};

// TODO Create skeleton loader for this component that inherits width and height from the parent component
export const CertificateDetailsPage = () => {
  useTracking();
  const [searchParams, setSearchParams] = useSearchParams();
  const isReady = searchParams.get('ready') === 'true';
  const [isReadyModalOpen, setIsReadyModalOpen] = useState(isReady);
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);
  const [isPdfOverrideOpen, setIsPdfOverrideOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const { isWtw } = usePlatform();
  const { policyId, certificateId } = useParams<CertificatesRouteParams>();
  const user = useUser();
  invariant(policyId, 'policyId is required');

  const isAuthorized = Boolean(user?.permissions.certificates?.view);
  const userId = user?.id;

  const isPdfEditingEnabled = useFeatureToggle('enable-pdf-editing');

  const cancelCertificateMutation = useCancelCertificate();

  const { data: certificate, refetch: refetchCertificate } = useQuery(
    certificateQueries.details({ policyId }),
  );

  const certificateUrlQuery = useQuery({
    queryKey: ['certificateUrl', policyId, certificate?.draft_certificate_url],
    queryFn: async () => {
      const certificateUrl = certificate?.draft_certificate_url;
      if (!policyId || !certificateUrl) return null;
      try {
        const response = await fetch(certificateUrl);
        const blob = await response.blob();
        return URL.createObjectURL(blob);
      } catch (error) {
        console.error('Error fetching certificate URL:', error);
        return null;
      }
    },
    enabled: !!certificate?.certificate_url,
  });

  const createCertificatePdfOverrides = useCreateCertificatePdfOverride({
    onSuccess: () => {
      refetchCertificate();
      setIsPdfOverrideOpen(false);
      setSearchParams(undefined, { replace: true });
    },
  });

  return (
    <SectionLayout
      isAuthorized={isAuthorized}
      data-testid="certificate-details-page"
      title={`Certificate ${certificateId}`}
      backLink="/certificates"
      actions={
        <CertificateActions
          onCancel={() => {
            setIsCancelModalOpen(true);
          }}
        />
      }
      tooltip={
        isWtw
          ? {
              trigger: <ActiveOpenCoverTooltip.Trigger />,
              content: <ActiveOpenCoverTooltip.Content />,
            }
          : undefined
      }
    >
      <div className="flex flex-col gap-6 w-full">
        <StatusBanner policyId={policyId} />
        <div className="w-full flex flex-row gap-6">
          <Suspense
            fallback={
              <div
                style={{ height: '330px', width: '100%' }}
                data-testid="skeleton-loader"
                className={styles.skeletonLoader}
              />
            }
          >
            <CertificateShipmentDetails policyId={policyId} />
          </Suspense>
        </div>
        <div className="w-full flex flex-row gap-6">
          <Suspense
            fallback={
              <div
                style={{ height: '330px', width: '100%' }}
                data-testid="skeleton-loader"
                className={styles.skeletonLoader}
              />
            }
          >
            <NamedAssuredDetails policyId={policyId} />
          </Suspense>
        </div>
        <Suspense
          fallback={
            <div
              style={{ height: '200px' }}
              className={styles.skeletonLoader}
            />
          }
        >
          <CertificateAdditionalClauses />
        </Suspense>
        <PollingFileList
          policyId={policyId}
          onEditCertificatePdfPress={() => setIsPdfOverrideOpen(true)}
        />
        <Suspense
          fallback={
            <div
              style={{ height: '100px' }}
              className={styles.skeletonLoader}
            />
          }
        >
          <CertificateClaims policyId={policyId} />
        </Suspense>
      </div>
      <CreateCertificateModal
        showActionButton={true}
        actionButton={
          isPdfEditingEnabled && (
            <Button
              variant="primary"
              size="medium"
              width="full"
              label="Preview & Edit Display Details"
              onPress={() => {
                setIsPdfOverrideOpen(true);
                setIsReadyModalOpen(false);
                setSearchParams(undefined, { replace: true });
              }}
            />
          )
        }
        data-testid="create-certificate-modal"
        isOpen={isReadyModalOpen}
        onOpenChange={(value) => {
          setIsReadyModalOpen(value);
          setSearchParams(undefined, {
            replace: true,
          });
        }}
        onExit={() =>
          setSearchParams(undefined, {
            replace: true,
          })
        }
      />
      <ActionConfirmationModal
        data-testid="cancel-certificate-modal"
        isOpen={isCancelModalOpen}
        onOpenChange={(value) => {
          setIsCancelModalOpen(value);
        }}
        onExit={() => setIsCancelModalOpen(false)}
        onConfirmChanges={() => {
          if (!userId) return;
          cancelCertificateMutation.mutate({
            reason: cancelReason,
            policyId: Number(policyId),
            userId,
          });
          setIsCancelModalOpen(false);
        }}
        confirmButtonDisabled={cancelReason.length < 3}
        heading={
          <div className="flex flex-col gap-2 self-start w-full">
            <Typography level="h3">
              Cancel Certificate {certificateId}
            </Typography>
            <Typography>
              Tell us the reason for cancelling this Certificate{' '}
              <span className="text-red-500 text-sm">*</span>
            </Typography>
            <Typography>
              <textarea
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
                className="w-full h-32 border border-gray-300 rounded-md p-2"
                placeholder="Type your reason here..."
              />
            </Typography>
          </div>
        }
      />
      {certificateUrlQuery.data && policyId && certificateId && (
        <CertificatePdfOverrideModal
          policyId={policyId}
          certificateId={certificateId}
          certificateUrl={certificateUrlQuery.data}
          isPdfOverideOpen={isPdfOverrideOpen}
          setIsPdfOverrideOpen={setIsPdfOverrideOpen}
          onSave={(data) => {
            createCertificatePdfOverrides.mutate({
              bffCreateCertificatePdfOverrideRequest: {
                policy_id: parseInt(policyId),
                overrides: data,
              },
            });
          }}
          isSaving={createCertificatePdfOverrides.isPending}
        />
      )}
    </SectionLayout>
  );
};
